import React, { useMemo, useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shopflo/ui'
import InputTextField from '../form-elements/InputTextField'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'

export interface SelectionOption {
  key: string
  value: string
  icon?: React.ReactNode
}

interface GenericSelectionModalProps {
  /** Array of options to display */
  options: SelectionOption[]
  /** Currently selected option key */
  selectedKey?: string
  /** Callback when an option is selected */
  onSelect: (option: SelectionOption) => void
  /** Placeholder text when no option is selected */
  placeholder?: string
  /** Placeholder component when no option is selected (custom trigger) */
  placeholderComponent?: React.ReactNode
  /** Whether to show search input */
  showSearch?: boolean
  /** Placeholder text for search input */
  searchPlaceholder?: string
  /** Empty state title */
  emptyStateTitle?: string
  /** Empty state description */
  emptyStateDescription?: string
  /** Custom class name for trigger */
  triggerClassName?: string
  /** Custom class name for dropdown content */
  contentClassName?: string
  /** Alignment of the dropdown relative to the trigger */
  align?: 'start' | 'center' | 'end'
}

export const GenericSelectionModal: React.FC<GenericSelectionModalProps> = ({
  options,
  selectedKey,
  onSelect,
  placeholder,
  placeholderComponent,
  showSearch = true,
  searchPlaceholder = 'Search...',
  emptyStateTitle,
  emptyStateDescription,
  triggerClassName = '',
  contentClassName = '',
  align
}) => {
  const { t } = useLocale()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options
    const lowerSearch = searchTerm.toLowerCase()
    return options.filter(option => option.value.toLowerCase().includes(lowerSearch))
  }, [options, searchTerm])

  const handleValueChange = (value: string) => {
    const option = options.find(opt => opt.key === value)
    if (option) {
      onSelect(option)
    }
  }

  const displayPlaceholder = placeholder || t('select_option')

  return (
    <Select value={selectedKey ?? ''} onValueChange={handleValueChange}>
      {placeholderComponent ? (
        <SelectTrigger renderType='basic' className={triggerClassName}>
          <>{placeholderComponent}</>
        </SelectTrigger>
      ) : (
        <SelectTrigger
          className={classNames('w-fit h-9 bg-white gap-2 rounded-lg border-coal-lighter', triggerClassName)}
        >
          <SelectValue placeholder={displayPlaceholder} />
        </SelectTrigger>
      )}
      <SelectContent align={align} className={classNames('w-64 max-h-[300px] overflow-y-auto', contentClassName)}>
        {showSearch && (
          <div className='p-2 border-b border-gray-100' onClick={e => e.stopPropagation()}>
            <InputTextField
              textFieldId='generic-selection-search'
              inputSize='small'
              inputValue={searchTerm}
              handleChange={e => setSearchTerm(e.target.value)}
              inputPlaceholder={searchPlaceholder}
            />
          </div>
        )}
        {filteredOptions.length > 0 ? (
          filteredOptions.map(option => (
            <SelectItem key={option.key} value={option.key}>
              <span className='flex min-w-0 items-center gap-2 text-inherit'>
                {option.icon}
                <span className='min-w-0 truncate'>{option.value}</span>
              </span>
            </SelectItem>
          ))
        ) : (
          <div className='p-4 text-center flex flex-col items-center justify-center gap-2'>
            <span className='text-text-sm-medium text-gray-700'>{emptyStateTitle || t('no_options_found')}</span>
            {emptyStateDescription && <p className='text-text-xs-regular text-gray-500'>{emptyStateDescription}</p>}
          </div>
        )}
      </SelectContent>
    </Select>
  )
}

export default GenericSelectionModal
